import React, { memo, useMemo } from 'react';
import { Popup } from 'react-map-gl';
import Button from '@/components/core/buttons/Button';
import { IoPaperPlane } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';
import { ProdottoType } from '../types';
import clsx from 'clsx';

interface ItineraryPopupProps {
  selectedItem: ProdottoType | null;
  onClose?: (item: null) => void;
}

const ItineraryPopup = ({ selectedItem, onClose }: ItineraryPopupProps) => {
  const { t } = useTranslation();

  const { id, image, lat, lng, url, title, excerpt, permalink } = selectedItem ?? {};

  const parseExcerpt = useMemo(() => {
    if (!excerpt) return '';

    if (excerpt.length < 100) return excerpt;

    return excerpt.slice(0, 100) + '...';
  }, [excerpt]);

  return (
    <Popup
      longitude={lng ?? 0}
      latitude={lat ?? 0}
      closeOnClick={false}
      onClose={() => {
        onClose?.(null);
      }}
      className={clsx('custom-popup relative z-[1000] bg-transparent animate-duration-500', {
        '!custom-popup-none': !selectedItem,
        'animate-fadeIn': selectedItem,
      })}
      closeButton={false}
      style={{
        backgroundColor: 'transparent',
      }}
    >
      {image?.url ? (
        <div className="relative mb-2 hidden aspect-video w-full lg:block">
          <Image
            blurDataURL="/placeholder.png"
            placeholder="blur"
            src={image?.url ?? '/images/placeholder.jpg'}
            alt={image?.title ?? title}
            className="h-full w-full rounded-xl object-cover"
            fill
          />
        </div>
      ) : null}

      <h3 className="mb-3 text-lg font-semibold leading-5">{title}</h3>
      <div
        className="line-clamp-4 hidden text-sm text-gray-600 dark:text-gray-200 lg:block [&>p>strong]:text-gray-600 dark:[&>p>strong]:text-gray-200 [&>p]:!text-sm [&>p]:text-gray-600 dark:[&>p]:text-gray-200 "
        dangerouslySetInnerHTML={{
          __html: parseExcerpt ?? '',
        }}
      />
      <Button
        variant="primary"
        size="sm"
        className="!ml-auto !mt-4 flex w-full items-center justify-center !px-4 !text-sm"
        onClick={() => window.open(permalink || url, '_blank')}
      >
        <IoPaperPlane className="mr-2 h-3.5 w-3.5" />
        {t('Scopri di più')}
      </Button>
    </Popup>
  );
};

export default memo(ItineraryPopup);
